"use client";

import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  Backdrop,
  CircularProgress,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Typography,
  Tabs,
  Tab,
  Box,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Skeleton,
  Alert,
  Tooltip,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

// Assuming these imports and types exist from the original project context
import {
  FileModel,
  getFileManagerFiles,
  getImagesCurrentPage,
  getImagesMaximumPage,
  getIsFileUploading,
  setImagesCurrentPage,
  setImagesMaximumPage,
  setIsFileUploading,
  setUpladedFiles,
  uploadFile,
} from "./reducers/advanced-file-manager/advanced-file-manager.reducer";
import sendApiRequest, { NetworkMethods } from "../../lib/Network";
import { getCurrentUser } from "../../store/reducers/user/user.reducer";

// Icons for better UX
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// --- Type Definitions ---
interface ComponentProps {
  isOpen: boolean;
  handleClose: () => void;
  handleImageFileSelection: (clickedItemUrl: string) => void;
  handleAudioFileSelection: (clickedItemUrl: string) => void;
}

// --- Helper Components for a Cleaner UI ---

// A reusable, responsive, and interactive card for displaying files
const FileCard = ({
  file,
  onSelect,
  onDelete,
}: {
  file: FileModel;
  onSelect: () => void;
  onDelete: () => void;
}) => {
  const isImage = (file.mimeType.includes("image") || file.mimeType.includes("webp"));

  return (
    <Card
      sx={{
        width: { xs: "calc(50% - 8px)", sm: "calc(33.33% - 11px)", md: 180 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardMedia
        sx={{
          height: 140,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.200",
        }}
      >
        {isImage ? (
          <img
            src={file.url}
            alt={file.url}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <AudioFileIcon sx={{ fontSize: 60, color: "grey.500" }} />
        )}
      </CardMedia>
      <CardContent sx={{ flexGrow: 1, p: 1 }}>
        <Typography
          variant="caption"
          sx={{
            wordBreak: "break-all",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {file.url.split("/").pop()}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: "space-between", p: 0.5 }}>
        <Tooltip title="Kiválaszt">
          <IconButton onClick={onSelect} size="small" color="primary">
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Törlés">
          <IconButton onClick={onDelete} size="small" color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

// A Skeleton Card for loading states
const SkeletonFileCard = () => (
  <Box
    sx={{
      width: { xs: "calc(50% - 8px)", sm: "calc(33.33% - 11px)", md: 180 },
    }}
  >
    <Skeleton variant="rectangular" height={140} />
    <Skeleton variant="text" sx={{ mt: 1 }} />
    <Skeleton variant="text" width="60%" />
  </Box>
);

// --- Main File Manager Component ---
export default function AdvancedFileManager({
  isOpen,
  handleClose,
  handleImageFileSelection,
  handleAudioFileSelection,
}: ComponentProps) {
  const dispatch = useDispatch();
  const currentUser = useSelector(getCurrentUser);
  const uploadedFiles = useSelector(getFileManagerFiles);
  const isUploading = useSelector(getIsFileUploading);
  const currentPage = useSelector(getImagesCurrentPage);
  const maxPage = useSelector(getImagesMaximumPage);

  const [tab, setTab] = useState(0); // 0: Browse, 1: Upload
  const [fileTypeTab, setFileTypeTab] = useState(0); // 0: Images, 1: Audio
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFiles = useCallback(
    async (page: number) => {
      if (!currentUser?.token) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await sendApiRequest({
          headers: { Authorization: "Bearer " + currentUser.token },
          method: NetworkMethods.GET,
          endpoint: `media?imagePage=${page}`,
        });

        if (!response.isSuccess)
          throw new Error("Hiba a fájlok lekérdezése során!");

        const responseData = response.data;
        dispatch(setImagesCurrentPage(page));
        dispatch(setImagesMaximumPage(responseData.image.pages));
        dispatch(setUpladedFiles(responseData.image.data));
      } catch (err) {
        setError("Hiba a fájlok lekérdezése során!");
        toast.error("Hiba a fájlok lekérdezése során!");
      } finally {
        setIsLoading(false);
      }
    },
    [currentUser, dispatch]
  );

  useEffect(() => {
    if (isOpen) {
      fetchFiles(1); // Fetch first page when modal opens
    }
  }, [isOpen, fetchFiles]);

  const handleUpload = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    dispatch(setIsFileUploading(true));
    const formData = new FormData();
    formData.append("mediaFile", file);

    try {
      const response = await sendApiRequest({
        headers: { Authorization: "Bearer " + currentUser.token },
        method: NetworkMethods.POST,
        endpoint: `media/upload`,
        payload: formData,
      });

      if (!response.isSuccess) throw new Error("Hiba a feltöltés során!");

      toast.success("Fájl sikeresen feltöltve!");
      await fetchFiles(1); // Refresh file list
      setTab(0); // Switch back to browser tab
    } catch (e) {
      toast.error("Hiba a feltöltés során!");
    } finally {
      dispatch(setIsFileUploading(false));
    }
  };

  const handleDelete = async (fileUrl: string) => {
    // Implement delete logic here, for example:
    const fileName = fileUrl.split("/").pop();
    if (
      !window.confirm(
        `Biztosan törölni szeretné a következő fájlt: ${fileName}?`
      )
    )
      return;

    toast.promise(
      sendApiRequest({
        method: NetworkMethods.POST,
        endpoint: `media/delete`,
        payload: {
          url: fileUrl,
        },
        headers: { Authorization: "Bearer " + currentUser.token },
      }).then((res) => {
        if (!res.isSuccess) throw new Error("Törlés sikertelen");
        fetchFiles(currentPage); // Refresh list
        return res;
      }),
      {
        loading: "Törlés folyamatban...",
        success: "Sikeres törlés!",
        error: "Hiba a törlés során!",
      }
    );
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleUpload,
    multiple: false,
  });

  const handlePageChange = (_: ChangeEvent<unknown>, page: number) => {
    fetchFiles(page);
  };

  const filesToShow = React.useMemo(() => {
    const typeFilter = fileTypeTab === 0 ? "image" : "audio";
    return uploadedFiles.filter((file: FileModel) => {
      if (fileTypeTab === 0) {
        return (
          file.mimeType.includes("image") || file.mimeType.includes("webp")
        );
      } else {
        return file.mimeType.includes("audio");
      }
    });
  }, [uploadedFiles, fileTypeTab]);

  return (
    <Backdrop
      open={isOpen}
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 9999 }}
    >
      <Paper
        sx={{
          width: "90vw",
          height: "90vh",
          maxWidth: "1200px",
          maxHeight: "800px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 2,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Typography variant="h5" fontWeight="bold">
            Fájlkezelő
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, newValue) => setTab(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            label="Fájlok"
            icon={<PhotoLibraryIcon />}
            iconPosition="start"
          />
          <Tab
            label="Feltöltés"
            icon={<UploadFileIcon />}
            iconPosition="start"
          />
        </Tabs>

        {/* File Browser Tab */}
        <Box hidden={tab !== 0} sx={{ p: 2, overflowY: "auto", flexGrow: 1 }}>
          <Tabs
            value={fileTypeTab}
            onChange={(_, newValue) => setFileTypeTab(newValue)}
            sx={{ mb: 2 }}
          >
            <Tab label="Képek" />
            <Tab label="Hangfájlok" />
          </Tabs>
          {isLoading ? (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {Array.from(new Array(8)).map((_, i) => (
                <SkeletonFileCard key={i} />
              ))}
            </Stack>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : filesToShow.length > 0 ? (
            <Stack spacing={3} alignItems="center">
              <Stack
                direction="row"
                flexWrap="wrap"
                gap={2}
                useFlexGap
                justifyContent="center"
              >
                {filesToShow.map((file) => (
                  <FileCard
                    key={file.url}
                    file={file}
                    onSelect={() => {
                      const handler =
                        fileTypeTab === 0
                          ? handleImageFileSelection
                          : handleAudioFileSelection;
                      handler(file.url);
                      toast.success("Fájl kiválasztva!");
                      handleClose();
                    }}
                    onDelete={() => handleDelete(file.url)}
                  />
                ))}
              </Stack>
              {maxPage > 1 && (
                <Pagination
                  count={maxPage}
                  page={currentPage}
                  onChange={handlePageChange}
                />
              )}
            </Stack>
          ) : (
            <Alert severity="info">
              Nincsenek feltöltött fájlok ebben a kategóriában.
            </Alert>
          )}
        </Box>

        {/* File Upload Tab */}
        <Box
          hidden={tab !== 1}
          sx={{
            p: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexGrow: 1,
          }}
        >
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed",
              borderColor: isDragActive ? "primary.main" : "grey.400",
              borderRadius: 2,
              p: 6,
              textAlign: "center",
              cursor: "pointer",
              transition: "border-color 0.3s, background-color 0.3s",
              bgcolor: isDragActive ? "action.hover" : "transparent",
              width: "100%",
              maxWidth: "600px",
            }}
          >
            <input {...getInputProps()} />
            <UploadFileIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />
            <Typography variant="h6">
              Húzd ide a fájlt, vagy kattints a feltöltéshez
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Kép- vagy hangfájlok engedélyezettek
            </Typography>
          </Box>
        </Box>

        {isUploading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Paper>
    </Backdrop>
  );
}
